import type { IncomeStatementRow, BalanceSheetRow, CashFlowRow } from '../../types/tickerDetail';

/**
 * Stack maths behind the Financials bar chart.
 *
 * Kept apart from the D3 component because this is the half that can be shown
 * correct. A stacked financial chart fails quietly: a segment computed as a
 * difference can come out negative (a loss-making year, a quarter where opex
 * exceeded gross profit), and a stack that silently drops or clamps it still
 * draws a plausible-looking bar. The numbers below decide what the reader
 * believes, so they are computed here and tested.
 */

/** One coloured span of a bar. */
export interface StackSegment {
  key: string;
  label: string;
  value: number;
  /** Baseline of the span on the value axis. */
  y0: number;
  /** Far edge of the span. Below `y0` when the value is negative. */
  y1: number;
}

/** One period's bar: its segments plus the extent they occupy. */
export interface StackBar {
  /** Period end date, as the feed states it (YYYY-MM-DD). */
  date: string;
  segments: StackSegment[];
  /** Lowest point any segment reaches. Zero or negative. */
  min: number;
  /** Highest point any segment reaches. Zero or positive. */
  max: number;
  /**
   * The figure the stack adds up to, named. Revenue on the income view,
   * liabilities plus equity on the balance view.
   *
   * The bar's height already shows this — it is the top of the stack — but a
   * height is not a number a reader can quote. Without it the tooltip lists
   * every part of revenue and never revenue itself, which is the first figure
   * anyone looks for.
   *
   * Null when no segment was valued, since a total of nothing is not zero.
   */
  total: { label: string; value: number } | null;
  /**
   * Value called out beside the bar rather than stacked into it — net income
   * on the income view, free cash flow on the cash-flow view. Null when the
   * view has none or the feed omitted it.
   */
  marker: { label: string; value: number } | null;
  /**
   * Value belonging to a series drawn across the bars rather than to this bar
   * alone — EBITDA on the income view. Null when the feed omitted it, which
   * breaks the line rather than interpolating across the gap.
   *
   * Kept apart from `marker` because the two are read differently: a marker
   * answers "what did this period earn", an overlay answers "which way is
   * this trending". Connecting markers would imply a continuity that a
   * per-period figure does not have.
   */
  overlay: number | null;
}

export type StackView = 'income' | 'balance' | 'cash_flow';

/** A segment definition before stacking: just a name and a signed amount. */
interface RawSegment {
  key: string;
  label: string;
  value: number;
}

/**
 * Stacks signed values away from zero, positives up and negatives down.
 *
 * Two separate cursors rather than one running total. With a single cursor a
 * negative segment would be drawn back over the positive span beneath it,
 * hiding part of a bar the reader is trying to compare. Splitting them means a
 * loss hangs below the axis where it is legible as a loss.
 */
function stackSegments(raw: RawSegment[]): Pick<StackBar, 'segments' | 'min' | 'max'> {
  let up = 0;
  let down = 0;
  const segments: StackSegment[] = [];

  for (const seg of raw) {
    // A missing figure is not a zero — drop it rather than draw a nil span.
    if (!Number.isFinite(seg.value) || seg.value === 0) continue;

    if (seg.value > 0) {
      segments.push({ ...seg, y0: up, y1: up + seg.value });
      up += seg.value;
    } else {
      segments.push({ ...seg, y0: down, y1: down + seg.value });
      down += seg.value;
    }
  }

  return { segments, min: down, max: up };
}

/** Difference of two figures, or NaN when either is missing. */
function diff(a: number | null | undefined, b: number | null | undefined): number {
  if (!Number.isFinite(a as number) || !Number.isFinite(b as number)) return NaN;
  return (a as number) - (b as number);
}

/** Finite number, or NaN when the feed omitted it. */
function num(v: number | null | undefined): number {
  return Number.isFinite(v as number) ? (v as number) : NaN;
}

/**
 * Revenue broken into where it went.
 *
 * The segments sum to revenue by construction — each is the gap between two
 * reported subtotals, so nothing is unaccounted for and the bar height is the
 * top line. Net income rides alongside as a marker instead of being stacked:
 * it sits below operating income by tax and interest, and stacking it would
 * make the bar taller than the revenue it is meant to represent.
 */
function incomeBar(row: IncomeStatementRow): StackBar {
  const raw: RawSegment[] = [
    { key: 'cogs', label: 'Cost of Revenue', value: diff(row.revenue, row.grossProfit) },
    { key: 'opex', label: 'Operating Expenses', value: diff(row.grossProfit, row.operatingIncome) },
    { key: 'operatingIncome', label: 'Operating Income', value: num(row.operatingIncome) },
  ];

  const netIncome = num(row.netIncome);
  const ebitda = num(row.ebitda);
  const stacked = stackSegments(raw);
  const revenue = num(row.revenue);
  return {
    date: row.date,
    ...stacked,
    // The reported top line, not the sum of the segments. They agree when
    // every subtotal was reported, but if one is missing the segments derived
    // from it drop out and their sum silently understates revenue — quoting
    // that as revenue would contradict the table directly below the chart.
    total: Number.isFinite(revenue)
      ? { label: 'Revenue', value: revenue }
      : stacked.segments.length
        ? { label: 'Revenue', value: stacked.max }
        : null,
    marker: Number.isFinite(netIncome) ? { label: 'Net Income', value: netIncome } : null,
    // EBITDA cannot be a segment: it sits above operating income because it
    // adds back depreciation and amortisation, so it overlaps the opex span
    // rather than sitting beside it. Stacking it would double-count D&A and
    // push the bar above the revenue it is meant to total.
    overlay: Number.isFinite(ebitda) ? ebitda : null,
  };
}

/**
 * The accounting identity, drawn: liabilities plus equity is total assets.
 *
 * Equity is taken as reported rather than derived from assets minus
 * liabilities. The two disagree for companies with minority interests, and the
 * reported figure is the one quoted everywhere else.
 */
function balanceBar(row: BalanceSheetRow): StackBar {
  const raw: RawSegment[] = [
    { key: 'liabilities', label: 'Total Liabilities', value: num(row.totalLiabilities) },
    { key: 'equity', label: 'Equity', value: num(row.totalStockholdersEquity) },
  ];

  const assets = num(row.totalAssets);
  const stacked = stackSegments(raw);
  return {
    date: row.date,
    ...stacked,
    // Liabilities plus equity. Deliberately not labelled "Total Assets" — the
    // marker already carries the reported assets figure, and the two differ
    // for companies with minority interests. Naming both the same would make
    // a real discrepancy look like a rendering bug.
    total: stacked.segments.length
      ? { label: 'Liabilities + Equity', value: stacked.max }
      : null,
    marker: Number.isFinite(assets) ? { label: 'Total Assets', value: assets } : null,
    overlay: null,
  };
}

/**
 * Cash generated against cash spent on the business.
 *
 * Capex arrives from the feed already negative, which is why it is passed
 * through unchanged: forcing a sign here would flip it back above the axis for
 * any feed that reports it positive, and the stacker puts negatives below zero
 * on its own.
 */
function cashFlowBar(row: CashFlowRow): StackBar {
  const raw: RawSegment[] = [
    { key: 'operatingCashFlow', label: 'Operating Cash Flow', value: num(row.operatingCashFlow) },
    { key: 'capex', label: 'Capital Expenditure', value: num(row.capitalExpenditure) },
  ];

  const fcf = num(row.freeCashFlow);
  const stacked = stackSegments(raw);
  return {
    date: row.date,
    ...stacked,
    // Operating cash flow less capex. The segments sit on opposite sides of
    // the axis here, so the total is their net rather than the top of the
    // stack — max alone would just restate operating cash flow.
    total: stacked.segments.length
      ? { label: 'Net Cash Flow', value: stacked.max + stacked.min }
      : null,
    marker: Number.isFinite(fcf) ? { label: 'Free Cash Flow', value: fcf } : null,
    overlay: null,
  };
}

/** Name of the overlay series per view, or null where there is none. */
export const OVERLAY_LABEL: Record<StackView, string | null> = {
  income: 'EBITDA',
  balance: null,
  cash_flow: null,
};

/** Colour key per segment, resolved by the component against the palette. */
export const SEGMENT_ORDER: Record<StackView, string[]> = {
  income: ['cogs', 'opex', 'operatingIncome'],
  balance: ['liabilities', 'equity'],
  cash_flow: ['operatingCashFlow', 'capex'],
};

/**
 * Bars for a view, oldest first.
 *
 * The feed returns newest-first; charts read left to right in time, so the
 * order is reversed here rather than in the component. Periods where every
 * segment was missing are dropped — an empty bar is a gap in the data, not a
 * period where the company reported nothing.
 */
export function buildStack(
  rows: IncomeStatementRow[] | BalanceSheetRow[] | CashFlowRow[],
  view: StackView
): StackBar[] {
  const build = (row: IncomeStatementRow | BalanceSheetRow | CashFlowRow): StackBar => {
    if (view === 'income') return incomeBar(row as IncomeStatementRow);
    if (view === 'balance') return balanceBar(row as BalanceSheetRow);
    return cashFlowBar(row as CashFlowRow);
  };

  return (rows as (IncomeStatementRow | BalanceSheetRow | CashFlowRow)[])
    .map(build)
    .filter((bar) => bar.segments.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Value-axis extent across every bar, padded to include zero.
 *
 * Zero is always in range so the baseline is visible: a chart of uniformly
 * positive bars that starts its axis at the smallest bar exaggerates small
 * differences into dramatic ones.
 */
export function stackExtent(bars: StackBar[]): [number, number] {
  let min = 0;
  let max = 0;
  for (const bar of bars) {
    if (bar.min < min) min = bar.min;
    if (bar.max > max) max = bar.max;
    // A marker outside the stacked span still has to fit on the axis.
    if (bar.marker) {
      if (bar.marker.value < min) min = bar.marker.value;
      if (bar.marker.value > max) max = bar.marker.value;
    }
    // So does the overlay — EBITDA routinely exceeds operating income, and
    // clipping it would draw the line flat along the top of the frame.
    if (bar.overlay != null) {
      if (bar.overlay < min) min = bar.overlay;
      if (bar.overlay > max) max = bar.overlay;
    }
  }
  return [min, max];
}

/** One unbroken run of the overlay line. */
export interface OverlayRun {
  /** Index into the bars array, so the caller can position by band. */
  index: number;
  value: number;
}

/**
 * The overlay split into runs that can be drawn as continuous lines.
 *
 * A period the feed did not value breaks the line rather than interpolating
 * across it. Joining the points either side of a gap would draw a confident
 * straight line through a year nobody reported, which reads as data rather
 * than as the absence of it. Runs of a single point are kept — the component
 * marks them with a dot, since a one-point line is invisible.
 */
export function overlayRuns(bars: StackBar[]): OverlayRun[][] {
  const runs: OverlayRun[][] = [];
  let current: OverlayRun[] = [];

  bars.forEach((bar, index) => {
    if (bar.overlay == null) {
      if (current.length) runs.push(current);
      current = [];
      return;
    }
    current.push({ index, value: bar.overlay });
  });

  if (current.length) runs.push(current);
  return runs;
}
